"use client"

import type React from "react"
import { useEffect, useRef } from "react"
import * as echarts from "echarts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useCostSavingsForecast, transformDataForCategoryChart } from "../Hooks/cost-savings-api-integration"

interface EChartsOption extends echarts.EChartsOption {}

const FutureCostSavings: React.FC = () => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  // Use API integration
  const {
    data: apiData,
    loading,
    error,
  } = useCostSavingsForecast({
    period: "yearly",
    scenarios: false,
    historical_days: 90,
  })

  useEffect(() => {
    if (chartRef.current) {
      chartInstance.current = echarts.init(chartRef.current)
    }

    // Handle resize events for responsiveness
    const handleResize = () => {
      if (chartRef.current && chartInstance.current) {
        chartInstance.current.resize()
      }
    }

    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
      if (chartInstance.current) {
        chartInstance.current.dispose()
        chartInstance.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!chartInstance.current) return

    if (!apiData || loading || error) {
      // Fallback to original dummy data if API fails
      const dummySavingsData = [
        { Year: 2018, Category: "Organic", "Cost Savings": 50 },
        { Year: 2018, Category: "Paper", "Cost Savings": 35 },
        { Year: 2018, Category: "PMD", "Cost Savings": 25 },
        { Year: 2019, Category: "Organic", "Cost Savings": 60 },
        { Year: 2019, Category: "Paper", "Cost Savings": 42.5 },
        { Year: 2019, Category: "PMD", "Cost Savings": 30 },
        { Year: 2020, Category: "Organic", "Cost Savings": 75 },
        { Year: 2020, Category: "Paper", "Cost Savings": 50 },
        { Year: 2020, Category: "PMD", "Cost Savings": 37.5 },
        { Year: 2021, Category: "Organic", "Cost Savings": 80 },
        { Year: 2021, Category: "Paper", "Cost Savings": 47.5 },
        { Year: 2021, Category: "PMD", "Cost Savings": 40 },
        { Year: 2022, Category: "Organic", "Cost Savings": 75 },
        { Year: 2022, Category: "Paper", "Cost Savings": 50 },
        { Year: 2022, Category: "PMD", "Cost Savings": 37.5 },
        { Year: 2023, Category: "Organic", "Cost Savings": 90 },
        { Year: 2023, Category: "Paper", "Cost Savings": 60 },
        { Year: 2023, Category: "PMD", "Cost Savings": 45 },
        { Year: 2024, Category: "Organic", "Cost Savings": 105 },
        { Year: 2024, Category: "Paper", "Cost Savings": 70 },
        { Year: 2024, Category: "PMD", "Cost Savings": 55 },
        { Year: 2025, Category: "Organic", "Cost Savings": 125 },
        { Year: 2025, Category: "Paper", "Cost Savings": 80 },
        { Year: 2025, Category: "PMD", "Cost Savings": 65 },
        { Year: 2026, Category: "Organic", "Cost Savings": 150 },
        { Year: 2026, Category: "Paper", "Cost Savings": 90 },
        { Year: 2026, Category: "PMD", "Cost Savings": 80 },
        { Year: 2027, Category: "Organic", "Cost Savings": 160 },
        { Year: 2027, Category: "Paper", "Cost Savings": 95 },
        { Year: 2027, Category: "PMD", "Cost Savings": 85 },
        { Year: 2028, Category: "Organic", "Cost Savings": 170 },
        { Year: 2028, Category: "Paper", "Cost Savings": 100 },
        { Year: 2028, Category: "PMD", "Cost Savings": 90 },
        { Year: 2029, Category: "Organic", "Cost Savings": 180 },
        { Year: 2029, Category: "Paper", "Cost Savings": 105 },
        { Year: 2029, Category: "PMD", "Cost Savings": 95 },
        { Year: 2030, Category: "Organic", "Cost Savings": 190 },
        { Year: 2030, Category: "Paper", "Cost Savings": 110 },
        { Year: 2030, Category: "PMD", "Cost Savings": 100 },
      ]

      renderChart(dummySavingsData)
      return
    }

    // Transform API data using the utility function
    const transformedData = transformDataForCategoryChart(apiData)
    renderChart(transformedData)
  }, [apiData, loading, error])

  const renderChart = (dummySavingsData: any[]) => {
    if (!chartInstance.current) return

    const wasteCategories = [...new Set(dummySavingsData.map((d) => d.Category))]
    const datasetWithFilters: echarts.DatasetComponentOption[] = []
    const seriesList: echarts.SeriesOption[] = []
    const categoryColors: { [key: string]: string } = {
      Organic: "#28a745",
      Paper: "#007bff",
      PMD: "#ffc107",
    }

    wasteCategories.forEach((category) => {
      const datasetId = `dataset_${category}`
      datasetWithFilters.push({
        id: datasetId,
        fromDatasetId: "dataset_raw",
        transform: {
          type: "filter",
          config: {
            and: [
              { dimension: "Year", gte: 2018 },
              { dimension: "Category", "=": category },
            ],
          },
        },
      })

      seriesList.push({
        type: "line",
        datasetId: datasetId,
        showSymbol: false,
        name: category,
        color: categoryColors[category] || "#666666",
        endLabel: {
          show: true,
          formatter: (params: any) => {
            return `${category}`
          },
        },
        labelLayout: {
          moveOverlap: "shiftY",
        },
        emphasis: {
          focus: "series",
        },
        encode: {
          x: "Year",
          y: "Cost Savings",
          label: ["Category", "Cost Savings"],
          itemName: "Year",
          tooltip: ["Cost Savings"],
        },
      })
    })

    const option: EChartsOption = {
      animationDuration: 10000,
      dataset: [
        {
          id: "dataset_raw",
          source: dummySavingsData,
        },
        ...datasetWithFilters,
      ],
      tooltip: {
        order: "valueDesc",
        trigger: "axis",
        valueFormatter: (value) => {
          if (typeof value === "number") {
            return value.toFixed(2) + " €"
          }
          return String(value) + " €"
        },
        confine: true,
      },
      xAxis: {
        type: "category",
        name: "Year",
        nameLocation: "middle",
        nameGap: 25,
        boundaryGap: false,
        axisLabel: {
          padding: [8, 0, 0, 0],
          fontSize: 10,
          interval: "auto",
          rotate: window.innerWidth < 500 ? 30 : 0,
        },
      },
      yAxis: {
        name: "Cost Savings",
        nameGap: 15,
        axisLabel: {
          formatter: "{value}",
          fontSize: 10,
        },
      },
      grid: {
        left: "15%",
        right: "5%",
        top: "5%",
        bottom: "20%",
        containLabel: true,
      },
      series: seriesList,
    }

    chartInstance.current.setOption(option)
    setTimeout(() => {
      chartInstance.current?.resize()
    }, 100)
  }

  return (
    <Card className="w-full h-full flex flex-col">
      <CardHeader className="pb-2 px-3 sm:px-6">
        <CardTitle className="text-base sm:text-lg text-center">Future Cost Savings</CardTitle>
        {loading && <p className="text-xs text-muted-foreground text-center">Loading forecast data...</p>}
        {error && <p className="text-xs text-red-500 text-center">Using fallback data</p>}
      </CardHeader>
      <CardContent className="px-3 sm:px-6 pt-0 pb-3 sm:pb-6 flex-grow flex flex-col">
        <div className="flex-grow min-h-[200px] sm:min-h-[240px]">
          <div ref={chartRef} className="w-full h-full" />
        </div>
      </CardContent>
    </Card>
  )
}

export default FutureCostSavings
